import { useQuery } from "@tanstack/react-query";
import ProductShareView, {
    type ShareConfigItem,
} from "@univdiam/ui/customComponents/CatalogProduct/ProductShareView";
import { prettyLabel } from "@univdiam/ui/lib/prettyLabel";
import { blindMediaUrl } from "@univdiam/ui/lib/blindMedia";
import type { ProductImage } from "@univdiam/ui/type";
import axios from "axios";
import { useParams } from "react-router-dom";

const EXCLUDED_KEYS = [
    "title",
    "id",
    "price",
    "attachments",
    "iframe_link",
    "instruction",
    "quantity",
    "combination",
    "customFields",
];

/**
 * Fetch share data from the PUBLIC endpoint (no auth required).
 * Uses a plain axios instance instead of the app's `api` (which triggers 401 redirects).
 */
const resolveShareToken = (token: string) => {
    return axios.get(`${import.meta.env.VITE_SERVER_URL}/api/cad/share/${token}`);
};

const ShareProduct = () => {
    const { token } = useParams();

    const { data, isLoading, isError, error } = useQuery({
        queryKey: ["product-share-cad", token],
        queryFn: async () => resolveShareToken(token!),
        enabled: !!token,
        retry: false,
    });

    const shareData = data?.data?.data;

    if (isLoading || isError) {
        return (
            <ProductShareView
                images={[]}
                config={[]}
                isLoading={isLoading}
                isError={isError}
                errorStatus={(error as any)?.response?.status}
            />
        );
    }

    if (!shareData) return null;

    const config: ShareConfigItem[] = Object.entries(shareData)
        .filter(([key]) => !EXCLUDED_KEYS.includes(key))
        .map(([key, value]) => ({
            label: prettyLabel(key),
            value: value as string,
        }));

    // Stored absolute, so the origin host would otherwise show in every <img src>.
    const images: ProductImage[] = (shareData?.attachments ?? []).map((image: ProductImage) => ({
        ...image,
        src: blindMediaUrl(image.src),
        thumbnail: image.thumbnail ? blindMediaUrl(image.thumbnail) : undefined,
    }));

    return (
        <ProductShareView
            images={images}
            iframeLink={blindMediaUrl(shareData?.iframe_link) || undefined}
            title={shareData?.title}
            price={shareData?.price ? Number(shareData.price) * Number(shareData?.quantity || 1) : undefined}
            config={config}
            customFields={shareData?.customFields}
        />
    );
};

export default ShareProduct;
